import React, { useRef, useState, useEffect } from 'react';
import { initI18n, useTranslation } from '../../shared/i18n/index';
import PublicPageLayout from '../../shared/components/PublicPageLayout';
import { getPlanFeatures } from '../../shared/config/planFeatures';

// Helper to format VND price
const formatPrice = (price) => {
  return new Intl.NumberFormat('vi-VN').format(price);
};

// Helper to calculate daily price from yearly price
const calculateDailyPriceFromYearly = (yearlyPrice) => {
  return Math.round(yearlyPrice / 365);
};

// Helper to calculate daily price from monthly price
const calculateDailyPriceFromMonthly = (monthlyPrice) => {
  return Math.round(monthlyPrice / 30);
};

// Pricing Tier Card Component
const PricingTierCard = ({
  tierName,
  badge,
  badgeClass,
  price,
  priceSubtext,
  showCurrency = false,
  features,
  ctaText,
  ctaLink,
  highlighted,
  t,
}) => (
  <div className="col-lg-4 mb-4">
    <div
      className={`card h-100 border-0 d-flex flex-column ${highlighted ? 'shadow-lg' : 'shadow-sm'}`}
      style={{
        borderRadius: '16px',
        transform: highlighted ? 'scale(1.05)' : 'none',
        zIndex: highlighted ? 1 : 0,
      }}
    >
      {/* Badge placeholder - maintains consistent height whether badge exists or not */}
      <div
        className={`text-center py-2 fw-semibold text-white ${badgeClass || ''}`}
        style={{
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          background: badge
            ? highlighted
              ? 'linear-gradient(to right, #667eea, #764ba2)'
              : '#6b7280'
            : 'transparent',
          minHeight: '40px',
          visibility: badge ? 'visible' : 'hidden',
        }}
      >
        {badge || '\u00A0'}
      </div>
      <div className="card-body p-4 d-flex flex-column flex-grow-1">
        {/* Header section with fixed height */}
        <div style={{ minHeight: '140px' }}>
          <h3 className="fw-bold mb-3" style={{ fontSize: '24px', color: '#111827' }}>
            {tierName}
          </h3>
          <div className="mb-4">
            <span className="fw-bold" style={{ fontSize: '36px', color: '#111827', whiteSpace: 'nowrap' }}>
              {price}
              {showCurrency && (
                <span className="text-muted ms-1" style={{ fontSize: '18px' }}>₫</span>
              )}
            </span>
            {priceSubtext && (
              <p className="text-muted small mb-0">{priceSubtext}</p>
            )}
          </div>
        </div>
        {/* Features section - grows to fill space */}
        <ul className="list-unstyled mb-4 flex-grow-1">
          {features.map((feature, index) => (
            <li key={index} className="d-flex align-items-start mb-2">
              {feature.enabled ? (
                <i
                  className="bi bi-check-circle-fill me-2 flex-shrink-0"
                  style={{ color: '#22c55e', marginTop: '2px' }}
                ></i>
              ) : (
                <i
                  className="bi bi-x-circle-fill me-2 flex-shrink-0 text-muted"
                  style={{ marginTop: '2px' }}
                ></i>
              )}
              <span className={feature.enabled ? '' : 'text-muted'}>{feature.name}</span>
            </li>
          ))}
        </ul>
        {/* CTA button at bottom */}
        <a
          href={ctaLink}
          className={`btn w-100 fw-semibold py-2 mt-auto ${
            highlighted ? 'btn-primary' : 'btn-outline-primary'
          }`}
          style={{
            borderRadius: '8px',
            background: highlighted
              ? 'linear-gradient(to right, #667eea, #764ba2)'
              : undefined,
            borderColor: highlighted ? 'transparent' : undefined,
          }}
        >
          {ctaText}
        </a>
      </div>
    </div>
  </div>
);

// Billing Toggle Component
const BillingToggle = ({ isYearly, onToggle, savingsPercent, t }) => (
  <div className="d-flex justify-content-center align-items-center gap-3 mb-5">
    <span
      className={`fw-medium ${!isYearly ? 'text-dark' : 'text-muted'}`}
      style={{ cursor: 'pointer' }}
      onClick={() => onToggle(false)}
    >
      {t('pricing.monthly')}
    </span>
    <div
      className="form-check form-switch"
      style={{ transform: 'scale(1.3)' }}
    >
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id="billingToggle"
        checked={isYearly}
        onChange={(e) => onToggle(e.target.checked)}
        style={{ cursor: 'pointer' }}
      />
    </div>
    <span
      className={`fw-medium ${isYearly ? 'text-dark' : 'text-muted'}`}
      style={{ cursor: 'pointer' }}
      onClick={() => onToggle(true)}
    >
      {t('pricing.yearly')}
      <span
        className="badge bg-success ms-2"
        style={{ fontSize: '12px' }}
      >
        {t('pricing.savePercent', { percent: savingsPercent })}
      </span>
    </span>
  </div>
);

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-bottom py-3">
    <button
      className="btn btn-link text-start w-100 d-flex justify-content-between align-items-center p-0 text-decoration-none"
      onClick={onToggle}
      style={{ color: '#111827' }}
    >
      <span className="fw-semibold" style={{ fontSize: '16px' }}>
        {question}
      </span>
      <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
    </button>
    {isOpen && (
      <p className="mt-2 mb-0" style={{ color: '#4b5563', lineHeight: '24px' }}>
        {answer}
      </p>
    )}
  </div>
);

// FAQ Section Component
const FAQSection = ({ t }) => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: t('pricing.faq.subscriptionQuestion'),
      answer: t('pricing.faq.subscriptionAnswer'),
    },
    {
      question: t('pricing.faq.cancelQuestion'),
      answer: t('pricing.faq.cancelAnswer'),
    },
    {
      question: t('pricing.faq.paymentQuestion'),
      answer: t('pricing.faq.paymentAnswer'),
    },
  ];

  return (
    <section className="py-5 bg-white">
      <div className="container" style={{ maxWidth: '768px' }}>
        <h2
          className="fw-bold text-center mb-4"
          style={{ fontSize: '30px', color: '#111827' }}
        >
          {t('pricing.faq.title')}
        </h2>
        <div>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFAQ === index}
              onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ loginPath, t }) => (
  <section
    className="py-5"
    style={{
      background: 'linear-gradient(16.88deg, #667eea 50%, #764ba2 120.71%)',
    }}
  >
    <div className="container py-4">
      <div className="text-center text-white">
        <h2 className="fw-bold mb-3" style={{ fontSize: '36px' }}>
          {t('pricing.cta.title')}
        </h2>
        <p
          className="mb-4 mx-auto"
          style={{ fontSize: '18px', maxWidth: '600px', color: '#f3f4f6' }}
        >
          {t('pricing.cta.description')}
        </p>
        <a
          href={loginPath}
          className="btn btn-light btn-lg fw-semibold px-4"
          style={{ borderRadius: '8px', color: '#667eea' }}
        >
          {t('pricing.cta.button')}
        </a>
      </div>
    </div>
  </section>
);

// Main Pricing Page Component
const PricingPage = ({ locale = 'vi', loginPath = '/login', flashNotice, flashAlert }) => {
  // Initialize i18n only once on mount
  const i18nInitialized = useRef(false);
  if (!i18nInitialized.current) {
    initI18n(locale);
    i18nInitialized.current = true;
  }
  const { t } = useTranslation();

  // State for billing toggle (default to monthly to match test expectations)
  const [isYearly, setIsYearly] = useState(false);

  // State for pricing data from API
  const [pricing, setPricing] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // Fetch pricing data from API
  useEffect(() => {
    fetch('/api/public/pricing')
      .then((res) => res.json())
      .then((data) => {
        if (data.pricing) {
          setPricing({
            proMonthlyPrice: data.pricing.pro_monthly_price,
            proYearlyPrice: data.pricing.pro_yearly_price,
            premiumMonthlyPrice: data.pricing.premium_monthly_price,
            premiumYearlyPrice: data.pricing.premium_yearly_price,
            yearlyDiscount: data.pricing.yearly_discount,
            freeActivitiesCount: data.pricing.free_activities_count,
            paidActivitiesCount: data.pricing.paid_activities_count,
          });
        }
        setPricingLoaded(true);
      })
      .catch(() => {
        // Use fallback defaults on error
        setPricing({
          proMonthlyPrice: 600000,
          proYearlyPrice: 5760000,
          premiumMonthlyPrice: 800000,
          premiumYearlyPrice: 7680000,
          yearlyDiscount: 20,
        });
        setPricingLoaded(true);
      });
  }, []);

  // Use the yearly discount from API (Setting.yearly_subscription_discount)
  const savingsPercent = pricing ? pricing.yearlyDiscount : 20;

  // Build activity counts object for dynamic feature labels
  const activityCounts = pricing ? {
    freeActivitiesCount: pricing.freeActivitiesCount,
    paidActivitiesCount: pricing.paidActivitiesCount,
  } : null;

  // Get features for each tier from shared config with dynamic activity counts
  const freeFeatures = getPlanFeatures('free', t, 'plan_features', activityCounts).map((f) => ({
    name: f.label,
    enabled: f.enabled,
  }));

  const proFeatures = getPlanFeatures('pro', t, 'plan_features', activityCounts).map((f) => ({
    name: f.label,
    enabled: f.enabled,
  }));

  const premiumFeatures = getPlanFeatures('premium', t, 'plan_features', activityCounts).map((f) => ({
    name: f.label,
    enabled: f.enabled,
  }));

  // Show loading state while pricing data is being fetched
  if (!pricingLoaded) {
    return (
      <PublicPageLayout
        loginPath={loginPath}
        flashNotice={flashNotice}
        flashAlert={flashAlert}
        t={t}
      >
        <div className="d-flex min-vh-100 align-items-center justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout
      loginPath={loginPath}
      flashNotice={flashNotice}
      flashAlert={flashAlert}
      t={t}
    >
      {/* Hero Section */}
      <section
        className="py-5"
        style={{
          background: 'linear-gradient(142.52deg, #faf5ff 0%, #eff6ff 70.71%)',
        }}
      >
        <div className="container py-4">
          <div className="text-center mb-5">
            <h1
              className="fw-bold mb-3"
              style={{ fontSize: '48px', color: '#111827', lineHeight: '56px' }}
            >
              {t('pricing.title')}
            </h1>
            <p
              className="mx-auto"
              style={{ fontSize: '20px', color: '#4b5563', maxWidth: '600px' }}
            >
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Billing Toggle */}
          <BillingToggle isYearly={isYearly} onToggle={setIsYearly} savingsPercent={savingsPercent} t={t} />

          {/* Pricing Cards */}
          <div className="row justify-content-center">
            {/* Free Tier */}
            <PricingTierCard
              tierName={t('pricing.freeTier')}
              price={t('pricing.freePrice')}
              features={freeFeatures}
              ctaText={t('pricing.cta.button')}
              ctaLink={loginPath}
              highlighted={false}
              t={t}
            />

            {/* Pro Tier - Highlighted */}
            <PricingTierCard
              tierName={t('pricing.proTier')}
              badge={t('pricing.mostPopular')}
              price={
                isYearly
                  ? formatPrice(pricing.proYearlyPrice)
                  : formatPrice(pricing.proMonthlyPrice)
              }
              showCurrency={true}
              priceSubtext={
                isYearly
                  ? t('pricing.pricePerDay', {
                      price: formatPrice(calculateDailyPriceFromYearly(pricing.proYearlyPrice)),
                    })
                  : t('pricing.pricePerDay', {
                      price: formatPrice(calculateDailyPriceFromMonthly(pricing.proMonthlyPrice)),
                    })
              }
              features={proFeatures}
              ctaText={t('pricing.cta.button')}
              ctaLink={loginPath}
              highlighted={true}
              t={t}
            />

            {/* Premium Tier */}
            <PricingTierCard
              tierName={t('pricing.premiumTier')}
              badge={t('pricing.bestValue')}
              badgeClass="bg-secondary"
              price={
                isYearly
                  ? formatPrice(pricing.premiumYearlyPrice)
                  : formatPrice(pricing.premiumMonthlyPrice)
              }
              showCurrency={true}
              priceSubtext={
                isYearly
                  ? t('pricing.pricePerDay', {
                      price: formatPrice(calculateDailyPriceFromYearly(pricing.premiumYearlyPrice)),
                    })
                  : t('pricing.pricePerDay', {
                      price: formatPrice(calculateDailyPriceFromMonthly(pricing.premiumMonthlyPrice)),
                    })
              }
              features={premiumFeatures}
              ctaText={t('pricing.cta.button')}
              ctaLink={loginPath}
              highlighted={false}
              t={t}
            />
          </div>

          {/* Per child note */}
          <p className="text-center text-muted mt-4">
            {t('pricing.perChildSubtitle')}
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection t={t} />

      {/* CTA Section */}
      <CTASection loginPath={loginPath} t={t} />
    </PublicPageLayout>
  );
};

export default PricingPage;
